import { useEffect } from 'react';

import { useRoomTuning } from '../../stores/room';
import { RoomAudio } from './RoomAudio';
import { stopListening } from './listening';
import { useRoom } from './useRoom';

/**
 * Keeps the followed broadcast fed, from wherever you are on the site.
 *
 * ## The thing this fixes
 *
 * Following used to live on the host's profile: the page polled the room, drove
 * the audio, and dropped the tuning when it unmounted. So navigating anywhere -
 * to a track, to your own profile, to the page of an artist you just heard -
 * silently ended the broadcast you were listening to. The audio carried on for
 * a while, unsynchronised and unlabelled, until the track finished and nothing
 * replaced it.
 *
 * That is the exact failure §9.1 exists to prevent, and the room store's own
 * doc claimed the opposite was true: *"you can leave a room page and keep
 * listening"*. You could not.
 *
 * Mounted in the shell beside `PlayerHost` and for the same structural reason:
 * it is outside the router outlet, so navigation cannot tear it down. There is
 * no clever state preservation here either - just position in the tree.
 *
 * ## It renders nothing and does nothing when nobody is tuned
 *
 * Which is almost always. The query only starts once somebody presses listen.
 */
export function RoomFollower() {
  const tuned = useRoomTuning((state) => state.tuned);

  if (!tuned) return null;

  return <Following username={tuned.username} />;
}

function Following({ username }: { username: string }) {
  /*
   * `listen` is owned here now, not by the page.
   *
   * The heartbeat is what makes the listener count real, and it should follow
   * the person rather than the route - somebody listening from their inbox is
   * as much in the room as somebody staring at the profile.
   */
  const { data } = useRoom(username, { listen: true });

  const room = data?.room ?? null;
  const live = room?.status === 'live';

  /*
   * The broadcast ending is not something the listener did, so it needs saying
   * rather than just going quiet.
   *
   * Deliberately after the room has actually reported `offline` rather than on
   * the first failed poll: a dropped request is not a broadcast ending, and
   * hanging up on somebody because their wifi blinked is worse than a few
   * seconds of silence.
   */
  useEffect(() => {
    if (room !== null && !live) stopListening();
  }, [room, live]);

  if (!room || !live) return null;

  return <RoomAudio room={room} enabled />;
}
